import { ArrowPathRoundedSquareIcon } from "@heroicons/react/24/solid";
import { useMemo } from "react";
import { Separator } from "@/core/components/ui/separator";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { formatDate, sortDateDescending } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { INTERNAL_TRANSACTION_TYPE } from "@/internal-transactions/constants";
import {
  useGetAccountSummary,
  useGetInternalTransactions,
} from "@/internal-transactions/query";
import { useGetPayouts } from "@/payouts/query";
import { PaymentsSummaryInfo } from "./payments-summary-info";

interface PaymentsSummaryProps {
  businessId?: string | null;
  onAutoRefillClick?: () => void;
}

const PaymentsSummary = ({
  businessId,
  onAutoRefillClick,
}: PaymentsSummaryProps) => {
  const { data: walletInfo, isLoading: isWalletInfoLoading } =
    useGetAccountSummary(businessId);
  const { data: referralPayouts = [], isLoading: isReferralPayoutsLoading } =
    useGetPayouts(businessId);
  const { data: transactions = [], isLoading: isTransactionsLoading } =
    useGetInternalTransactions(businessId);

  const lastReferralPayout = useMemo(
    () =>
      [...referralPayouts].sort((a, b) =>
        sortDateDescending(a.created_at, b.created_at),
      )[0] || null,
    [referralPayouts],
  );

  const lastDeposit = useMemo(
    () =>
      [...transactions]
        .filter((t) => t.type === INTERNAL_TRANSACTION_TYPE.deposit)
        .sort((a, b) => sortDateDescending(a.created_at, b.created_at))[0] ||
      null,
    [transactions],
  );

  const hasWalletInfo = !isWalletInfoLoading && walletInfo;
  const hasAutoRefill = walletInfo?.settings?.auto_refill_active;

  return (
    <Stack fullWidth direction="row" align="center" gap="md" className="px-6">
      <Stack className="p-3 pl-4 pr-8" gap="none" align="center">
        {isWalletInfoLoading && (
          <>
            <Skeleton className="h-8 w-[146px]" />
            <Skeleton className="mt-1 h-4 w-[100px]" />
          </>
        )}
        {hasWalletInfo && (
          <>
            <Stack direction="row" align="center" gap="xs">
              <Typography variant="titleW2" className="text-aqua-50">
                {formatMoneyCents(walletInfo?.wallet_balance || 0)}
              </Typography>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onAutoRefillClick}
                    className="cursor-pointer"
                  >
                    <ArrowPathRoundedSquareIcon
                      className={
                        hasAutoRefill
                          ? "h-6 w-6 text-interactive-text"
                          : "h-6 w-6 text-tertiary-text"
                      }
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {hasAutoRefill
                      ? "Auto-refill enabled"
                      : "Set up auto-refill"}
                  </p>
                </TooltipContent>
              </Tooltip>
            </Stack>
            <Typography variant="subtitleW3" textColor="tertiary">
              Account balance
            </Typography>
          </>
        )}
      </Stack>

      {hasWalletInfo && <Separator orientation="vertical" className="h-full" />}

      <PaymentsSummaryInfo
        isLoading={isReferralPayoutsLoading}
        value={formatMoneyCents(lastReferralPayout?.amount || 0)}
        label="Last referral payout"
        date={formatDate(lastReferralPayout?.created_at || "")}
        show={!!lastReferralPayout}
      />

      {!!lastReferralPayout && (
        <Separator orientation="vertical" className="h-full" />
      )}

      <PaymentsSummaryInfo
        isLoading={isTransactionsLoading}
        value={formatMoneyCents(lastDeposit?.amount || 0)}
        label="Last deposit"
        date={formatDate(lastDeposit?.created_at || "")}
        show={!!lastDeposit}
      />
    </Stack>
  );
};

export default PaymentsSummary;
